import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ExternalLink } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { IMAGES, LINKS } from "@/data/content";

const Hero = ({ onExplore }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [-2, 2]);

  return (
    <header ref={ref} className="relative overflow-hidden pb-16 pt-14 sm:pt-20">
      <div className="spotlight pointer-events-none absolute inset-x-0 top-0 h-[520px]" />
      <div className="relative mx-auto max-w-md px-5 sm:max-w-2xl lg:max-w-5xl lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-pink-200 bg-white/70 px-4 py-1.5 text-xs font-bold tracking-[0.18em] uppercase text-rosa-dark"
              data-testid="hero-eyebrow"
            >
              Cake Design · Pasta di Zucchero
            </motion.p>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-cacao sm:text-5xl lg:text-6xl">
              <MaskedLines
                delay={0.15}
                lines={["L'arte dolce", "si modella"]}
              />
              <MaskedLines
                delay={0.45}
                lines={["con le mani."]}
                lineClassName="italic text-rosa-dark"
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-6 max-w-md text-base text-cacao-muted leading-relaxed"
            >
              Ricette, tecniche e personaggi in pasta di zucchero: impara a
              trasformare biscotti e torte in piccole opere d'arte, un
              passo alla volta.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-calcolatore"
                onClick={onExplore}
                className="group inline-flex items-center gap-2 rounded-full bg-rosa px-6 py-3 text-sm font-bold text-white shadow-[0_10px_30px_rgba(232,122,144,0.4)] transition-colors hover:bg-rosa-dark"
              >
                Vai al calcolatore
                <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              </button>
              <a
                data-testid="hero-cta-corso"
                href={LINKS.hotmart}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-cacao/15 bg-white/70 px-6 py-3 text-sm font-bold text-cacao transition-colors hover:border-rosa hover:text-rosa-dark"
              >
                Scopri il corso
                <ExternalLink className="h-4 w-4" />
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="mt-6 font-hand text-2xl text-rosa-dark"
            >
              fatto a mano, con amore ♥
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-sm"
          >
            <div className="absolute -inset-6 rounded-[2.5rem] bg-blush/60 blur-2xl" />
            <motion.div
              style={{ y: imgY, rotate: imgRotate }}
              className="animate-float-soft relative overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_30px_60px_rgba(45,36,36,0.2)]"
            >
              <img
                src={IMAGES.hero}
                alt="Torta artigianale decorata a mano con pasta di zucchero"
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <div className="absolute -bottom-5 -left-4 rounded-2xl border border-pink-100 bg-white/95 px-4 py-2.5 shadow-lg backdrop-blur">
              <p className="text-[10px] font-bold uppercase tracking-widest text-cacao-light">
                Questa settimana
              </p>
              <p className="font-display text-sm font-bold text-cacao">
                Pentolone della Strega
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </header>
  );
};

export default Hero;
